import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MarketInsights = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('1W');
  const [marketNews, setMarketNews] = useState([
    {
      id: 1,
      title: 'EU ETS Phase 4 Allocation Update',
      summary: 'European Commission announces revised allocation methodology for 2025-2030 period, potentially reducing free allowances by 15%.',
      source: 'Carbon Pulse',
      timestamp: new Date(Date.now() - 3600000),
      impact: 'bullish',
      relevance: 'high'
    },
    {
      id: 2,
      title: 'German Industrial Output Rises',
      summary: 'Manufacturing sector growth drives increased energy demand, potentially boosting EUA consumption in Q4.',
      source: 'Reuters',
      timestamp: new Date(Date.now() - 7200000),
      impact: 'bullish',
      relevance: 'medium'
    },
    {
      id: 3,
      title: 'Weather Forecast: Mild Winter Expected',
      summary: 'Long-range forecasts suggest warmer than average temperatures across Europe, potentially reducing heating demand.',
      source: 'Bloomberg',
      timestamp: new Date(Date.now() - 10800000),
      impact: 'bearish',
      relevance: 'medium'
    },
    {
      id: 4,
      title: 'ECB Interest Rate Decision',
      summary: 'European Central Bank maintains rates, supporting economic growth and industrial activity.',
      source: 'Financial Times',
      timestamp: new Date(Date.now() - 14400000),
      impact: 'neutral',
      relevance: 'low'
    }
  ]);

  const [technicalIndicators, setTechnicalIndicators] = useState({
    rsi: 62.4,
    macd: 1.25,
    bollinger: {
      upper: 88.50,
      middle: 85.42,
      lower: 82.34,
      position: 'middle'
    },
    support: 82.00,
    resistance: 89.50,
    trend: 'bullish',
    momentum: 'positive'
  });

  const [marketSentiment, setMarketSentiment] = useState({
    overall: 'bullish',
    score: 72,
    factors: [
      { name: 'Supply Constraints', impact: 'bullish', weight: 25 },
      { name: 'Industrial Demand', impact: 'bullish', weight: 20 },
      { name: 'Weather Patterns', impact: 'bearish', weight: 15 },
      { name: 'Regulatory Changes', impact: 'bullish', weight: 30 },
      { name: 'Economic Outlook', impact: 'neutral', weight: 10 }
    ]
  });

  const [priceTargets, setPriceTargets] = useState([
    { timeframe: '1 Week', target: 87.50, confidence: 75, direction: 'up' },
    { timeframe: '1 Month', target: 92.00, confidence: 68, direction: 'up' },
    { timeframe: '3 Months', target: 95.50, confidence: 55, direction: 'up' },
    { timeframe: '6 Months', target: 88.00, confidence: 45, direction: 'neutral' }
  ]);

  const timeframes = ['1D', '1W', '1M', '3M'];

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'bullish': return 'text-success bg-success/10';
      case 'bearish': return 'text-error bg-error/10';
      case 'neutral': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getRelevanceColor = (relevance) => {
    switch (relevance) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'bullish': return 'text-success';
      case 'bearish': return 'text-error';
      case 'neutral': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 70) return 'text-success';
    if (confidence >= 50) return 'text-warning';
    return 'text-error';
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ago`;
    }
    return `${minutes}m ago`;
  };

  return (
    <div className="space-y-6">
      {/* Market News */}
      <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Market News & Analysis</h2>
          <Button variant="outline" size="sm">
            <Icon name="RefreshCw" size={16} strokeWidth={2} className="mr-2" />
            Refresh
          </Button>
        </div>

        <div className="space-y-4">
          {marketNews?.map((news) => (
            <div key={news?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-medium text-foreground">{news?.title}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactColor(news?.impact)}`}>
                      {news?.impact}
                    </span>
                    <span className={`text-xs font-medium ${getRelevanceColor(news?.relevance)}`}>
                      {news?.relevance} relevance
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{news?.summary}</p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>{news?.source}</span>
                    <span>{formatTimeAgo(news?.timestamp)}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  <Icon name="ExternalLink" size={16} strokeWidth={2} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Technical Analysis */}
      <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Technical Analysis</h3>
          <div className="flex items-center space-x-2">
            {timeframes?.map((tf) => (
              <button
                key={tf}
                onClick={() => setSelectedTimeframe(tf)}
                className={`px-3 py-1 rounded text-sm font-medium transition-maritime ${
                  selectedTimeframe === tf
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Key Indicators</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">RSI (14)</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-foreground">{technicalIndicators?.rsi}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      technicalIndicators?.rsi > 70 ? 'bg-error/10 text-error' :
                      technicalIndicators?.rsi < 30 ? 'bg-success/10 text-success': 'bg-warning/10 text-warning'
                    }`}>
                      {technicalIndicators?.rsi > 70 ? 'Overbought' :
                       technicalIndicators?.rsi < 30 ? 'Oversold' : 'Neutral'}
                    </span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">MACD</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${
                      technicalIndicators?.macd > 0 ? 'text-success' : 'text-error'
                    }`}>
                      {technicalIndicators?.macd > 0 ? '+' : ''}{technicalIndicators?.macd?.toFixed(2)}
                    </span>
                    <Icon 
                      name={technicalIndicators?.macd > 0 ? 'TrendingUp' : 'TrendingDown'} 
                      size={14} 
                      className={technicalIndicators?.macd > 0 ? 'text-success' : 'text-error'}
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Support</span>
                  <span className="text-sm font-medium text-foreground">€{technicalIndicators?.support?.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Resistance</span>
                  <span className="text-sm font-medium text-foreground">€{technicalIndicators?.resistance?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Bollinger Bands</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Upper</span>
                  <span className="font-mono">€{technicalIndicators?.bollinger?.upper?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Middle</span>
                  <span className="font-mono font-medium">€{technicalIndicators?.bollinger?.middle?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Lower</span>
                  <span className="font-mono">€{technicalIndicators?.bollinger?.lower?.toFixed(2)}</span>
                </div>
                <div className="mt-2 pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    Price position: <span className="font-medium text-foreground">{technicalIndicators?.bollinger?.position}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Market Sentiment</h4>
              <div className="text-center mb-4">
                <div className={`text-3xl font-bold ${getSentimentColor(marketSentiment?.overall)}`}>
                  {marketSentiment?.score}
                </div>
                <div className="text-sm text-muted-foreground">Sentiment Score</div>
                <div className={`text-sm font-medium ${getSentimentColor(marketSentiment?.overall)}`}>
                  {marketSentiment?.overall?.toUpperCase()}
                </div>
              </div>
              
              <div className="space-y-2">
                {marketSentiment?.factors?.map((factor, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{factor?.name}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-muted-foreground">{factor?.weight}%</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getImpactColor(factor?.impact)}`}>
                        {factor?.impact}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Price Targets</h4>
              <div className="space-y-3">
                {priceTargets?.map((target, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-foreground">{target?.timeframe}</div>
                      <div className="text-xs text-muted-foreground">
                        Confidence: <span className={getConfidenceColor(target?.confidence)}>{target?.confidence}%</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-foreground">€{target?.target?.toFixed(2)}</div>
                      <div className="flex items-center space-x-1">
                        <Icon 
                          name={target?.direction === 'up' ? 'TrendingUp' : 
                                target?.direction === 'down' ? 'TrendingDown' : 'Minus'} 
                          size={12} 
                          className={target?.direction === 'up' ? 'text-success' : 
                                   target?.direction === 'down' ? 'text-error' : 'text-muted-foreground'}
                        />
                        <span className="text-xs text-muted-foreground">{target?.direction}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketInsights;